import { createAppThunk } from '@mse-reg/app/store';
import { IPriorityClient, PriorityDto } from '@mse-reg/clients';
import { insertItem, removeItem, updateItem } from '@mse-reg/util';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Async Thunks
const fetchPrioritiesAsync = createAppThunk(
  'priorities/FETCH_PRIORITIES',
  async (_: void, deps: { priorityClient: IPriorityClient }) => {
    return await deps.priorityClient.list();
  },
  (r) => ({ priorityClient: r.resolve<IPriorityClient>('IPriorityClient') }),
);

const updatePriorityAsync = createAppThunk(
  'priorities/UPDATE_PRIORITY',
  async (priorityDto: PriorityDto, deps: { priorityClient: IPriorityClient }) => {
    await deps.priorityClient.update(priorityDto);
    return priorityDto;
  },
  (r) => ({ priorityClient: r.resolve<IPriorityClient>('IPriorityClient') }),
);

const deletePriorityAsync = createAppThunk(
  'priorities/DELETE_PRIORITY',
  async (id: number, deps: { priorityClient: IPriorityClient }, dispatch) => {
    await deps.priorityClient.delete(id);
    await dispatch(fetchPrioritiesAsync());
    return id;
  },
  (r) => ({ priorityClient: r.resolve<IPriorityClient>('IPriorityClient') }),
);

const addPriorityAsync = createAppThunk(
  'priorities/ADD_PRIORITY',
  async (priorityDto: PriorityDto, deps: { priorityClient: IPriorityClient }, dispatch) => {
    const result = await deps.priorityClient.add(priorityDto);
    dispatch(prioritiesActions.selectPriority(result.id));
    return result;
  },
  (r) => ({ priorityClient: r.resolve<IPriorityClient>('IPriorityClient') }),
);

// State
const initialState = {
  loading: false,
  errorMessage: null as string | null,
  priorities: [] as PriorityDto[],
  selectedPriority: null as PriorityDto | null,
};

export type PriorityManagementState = Readonly<typeof initialState>;

const slice = createSlice({
  name: 'priorities',
  initialState,
  reducers: {
    selectPriority(state, action: PayloadAction<number>) {
      if (state.selectedPriority?.id === action.payload) {
        return;
      }
      const priority = state.priorities.find((p) => p.id === action.payload);
      state.selectedPriority = priority ?? null;
    },
    memorizePriorityChange(state, action: PayloadAction<PriorityDto>) {
      state.priorities = updateItem(state.priorities, action.payload);
    },
    createNewPriority(state) {
      if (state.priorities.some((priority) => priority.id === 0)) return;

      const priority: PriorityDto = {
        id: 0,
        isActive: true,
        name: '',
        weighting: 5,
        colorCode: 'rgba(255, 255, 255, 1)',
        canBeDeleted: true,
        description: undefined,
      };
      state.priorities = insertItem(state.priorities, priority);
    },
    cancelNewPriority(state) {
      state.priorities = state.priorities.filter((priority) => priority.id !== 0);
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchPrioritiesAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPrioritiesAsync.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message ?? null;
      })
      .addCase(fetchPrioritiesAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.priorities = action.payload;
      })
      .addCase(addPriorityAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(addPriorityAsync.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message ?? null;
      })
      .addCase(addPriorityAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.priorities = state.priorities.map((p) => (p.id === 0 ? action.payload : p));
      })
      .addCase(updatePriorityAsync.pending, (state, action) => {
        state.loading = true;
        state.priorities = state.priorities.map((p) => (p.id === 0 ? action.meta.arg : p));
      })
      .addCase(updatePriorityAsync.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message ?? null;
      })
      .addCase(updatePriorityAsync.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deletePriorityAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(deletePriorityAsync.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message ?? null;
      })
      .addCase(deletePriorityAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.priorities = removeItem(state.priorities, action.payload);
      });
  },
});

export const prioritiesReducer = slice.reducer;
export const prioritiesActions = slice.actions;
export const prioritiesThunks = {
  fetchPrioritiesAsync,
  updatePriorityAsync,
  deletePriorityAsync,
  addPriorityAsync,
};

// Legacy module compatibility
const initActions = () => {
  return Object.freeze({
    selectPriority: (priorityId: number) => slice.actions.selectPriority(priorityId),
    memorizePriorityChange(field: string, value: any, priorityDto: PriorityDto) {
      const changedPriority = { ...priorityDto, [field]: value };
      return slice.actions.memorizePriorityChange(changedPriority);
    },
    createNewPriority: () => slice.actions.createNewPriority(),
    cancelNewPriority: () => slice.actions.cancelNewPriority(),
    fetchPrioritiesAsync: () => fetchPrioritiesAsync(),
    updatePriorityAsync: (priorityDto: PriorityDto) => updatePriorityAsync(priorityDto),
    deletePriorityAsync: (id: number) => deletePriorityAsync(id),
    addPriorityAsync: (priorityDto: PriorityDto) => addPriorityAsync(priorityDto),
  });
};

export type PriorityManagementActions = ReturnType<typeof initActions>;

export const prioritiesModule = {
  initActions: () => initActions(),
  reducer: slice.reducer,
};
